import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const NEWSLETTER_URL = 'https://readdy.ai/api/form/d7i08s7hkiob2r09iqvg';

const navLinks = [
  { label: 'Inicio', path: '/' },
  { label: 'Sobre Nosotros', path: '/sobre-nosotros' },
  { label: 'Donar', path: '/donar' },
  { label: 'Contacto', path: '/contacto' },
];

const services = [
  'Madera Plástica Posconsumo',
  'Recogida de Muebles y Llantas',
  'Recuperación de Zonas Verdes',
  'Mantenimiento de Alamedas',
  'Canchas y Recreación',
  'Aparatos Electrónicos (RAEE)',
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleNewsletter = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch(NEWSLETTER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#1b5e20] w-full mt-0">
      {/* Top green band with real photo accent */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://storage.readdy-site.link/project_files/99b3bf7a-f106-44a8-ac25-700226859767/c0d5e16a-caaa-43ca-9788-2669876b9ca3_WhatsApp-Image-2026-04-19-at-12.03.40.jpeg?v=870f0375775ede75a26e9d77694cefec')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>

        <div className="relative z-10 px-6 md:px-14 pt-16 pb-6">
          {/* Logo + tagline */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-14 pb-10 border-b border-white/15">
            <div className="flex items-center gap-4">
              <img
                src="https://static.readdy.ai/image/04072f05a7ab05cce1b5fc08d2859ba4/b0395c77c26132b4275f62d8aa13c8ff.png"
                alt="Fundación Frais"
                className="h-20 w-auto object-contain"
                style={{ filter: 'brightness(1.1)' }}
              />
              <div>
                <p
                  className="text-white font-bold text-lg leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Fundación Frais
                </p>
                <p
                  className="text-[#a5d6a7] text-xs tracking-widest uppercase mt-1"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Reciclaje en Acción Inmediata · Santander
                </p>
              </div>
            </div>
            {/* Social icons */}
            <div className="flex items-center gap-3">
              {[
                { icon: 'ri-instagram-line', label: 'Instagram', href: '#' },
                { icon: 'ri-facebook-box-line', label: 'Facebook', href: '#' },
                { icon: 'ri-whatsapp-line', label: 'WhatsApp', href: '#' },
                { icon: 'ri-youtube-line', label: 'YouTube', href: '#' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  rel="nofollow"
                  aria-label={s.label}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#a5d6a7] hover:text-[#1b5e20] text-white transition-all duration-300 cursor-pointer"
                >
                  <i className={`${s.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Main footer grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">

            {/* Col 1 — About */}
            <div>
              <p
                className="text-[#a5d6a7] text-[10px] tracking-[0.4em] uppercase font-semibold mb-5"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Quiénes somos
              </p>
              <p
                className="text-white/60 text-sm leading-relaxed mb-5"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                La Fundación de Reciclaje en Acción Inmediata de Santander transforma residuos y recupera espacios públicos en los 87 municipios del departamento desde 2022.
              </p>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-white/50 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <i className="ri-map-pin-line text-[#a5d6a7]"></i>
                  Petroriente, Giron - Santander, Colombia
                </div>
                <div className="flex items-center gap-2 text-white/50 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <i className="ri-calendar-check-line text-[#a5d6a7]"></i>
                  Operando desde 2022
                </div>
                <div className="flex items-center gap-2 text-white/50 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <i className="ri-government-line text-[#a5d6a7]"></i>
                  87 municipios activos
                </div>
              </div>
            </div>

            {/* Col 2 — Navigation */}
            <div>
              <p
                className="text-[#a5d6a7] text-[10px] tracking-[0.4em] uppercase font-semibold mb-5"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Navegación
              </p>
              <ul className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="flex items-center gap-2 text-white/65 text-sm hover:text-[#a5d6a7] transition-colors cursor-pointer group"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      <i className="ri-arrow-right-s-line text-xs text-[#a5d6a7]/50 group-hover:text-[#a5d6a7] transition-colors"></i>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 3 — Services */}
            <div>
              <p
                className="text-[#a5d6a7] text-[10px] tracking-[0.4em] uppercase font-semibold mb-5"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Nuestros Servicios
              </p>
              <ul className="flex flex-col gap-3">
                {services.map((svc) => (
                  <li key={svc}>
                    <span
                      className="flex items-center gap-2 text-white/65 text-sm"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      <i className="ri-checkbox-circle-line text-xs text-[#a5d6a7]/60"></i>
                      {svc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 4 — Newsletter */}
            <div>
              <p
                className="text-[#a5d6a7] text-[10px] tracking-[0.4em] uppercase font-semibold mb-5"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Únete al Movimiento
              </p>
              <p
                className="text-white/55 text-sm leading-relaxed mb-5"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Recibe noticias de impacto ambiental y proyectos de la fundación directamente en tu correo.
              </p>
              {submitted ? (
                <div className="flex items-center gap-2 text-[#a5d6a7] text-sm">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  <span style={{ fontFamily: "'Montserrat', sans-serif" }}>¡Gracias por suscribirte!</span>
                </div>
              ) : (
                <form
                  onSubmit={handleNewsletter}
                  data-readdy-form
                  id="newsletter-footer"
                  className="flex flex-col gap-3"
                >
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@correo.com"
                    required
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-[#a5d6a7] transition-colors"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-[#a5d6a7] text-[#1b5e20] text-xs font-bold tracking-wider uppercase hover:bg-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {loading ? 'Enviando...' : (
                      <>
                        Suscribirme
                        <i className="ri-mail-send-line"></i>
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Contact quick */}
              <div className="flex flex-col gap-2 mt-6">
                <a
                  href="mailto:Fundacion.frais@gmail.com"
                  className="flex items-center gap-2 text-white/50 text-xs hover:text-[#a5d6a7] transition-colors cursor-pointer"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <i className="ri-mail-line text-[#a5d6a7]/60"></i>
                  fundacion.frais@gmail.com
                </a>
                <a
                  href="tel:+573125819210"
                  className="flex items-center gap-2 text-white/50 text-xs hover:text-[#a5d6a7] transition-colors cursor-pointer"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <i className="ri-phone-line text-[#a5d6a7]/60"></i>
                  304 213 3550 - 312 581 9210
                </a>
                <a
                  href="https://wa.me/573046799009"
                  className="flex items-center gap-2 text-white/50 text-xs hover:text-[#a5d6a7] transition-colors cursor-pointer"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <i className="ri-whatsapp-line text-[#a5d6a7]/60"></i>
                  304 679 9009
                </a>
              </div>
            </div>
          </div>

          {/* Impact bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 p-6 rounded-2xl bg-white/5 border border-white/10">
            {[
              { value: '87', label: 'Municipios', icon: 'ri-map-2-line' },
              { value: '2022', label: 'Desde', icon: 'ri-calendar-line' },
              { value: '18K+', label: 'Personas', icon: 'ri-group-line' },
              { value: '320+', label: 'Instituciones', icon: 'ri-building-2-line' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#a5d6a7]/15 flex items-center justify-center flex-shrink-0">
                  <i className={`${stat.icon} text-[#a5d6a7] text-sm`}></i>
                </div>
                <div>
                  <div
                    className="text-white font-bold text-lg leading-none"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-white/40 text-[10px] tracking-wider uppercase"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-7">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p
                className="text-white/30 text-xs text-center"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                © 2026 Fundación de Reciclaje en Acción Inmediata de Santander. Todos los derechos reservados.
              </p>
              <div className="flex items-center gap-5">
                {['Política de Privacidad', 'Términos de Uso'].map((item) => (
                  <a
                    key={item}
                    href="#"
                    rel="nofollow"
                    className="text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {item}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
